import { CacheProvider } from '@emotion/react';
import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider } from '@mui/material/styles';
import throttle from 'lodash/throttle';
import Head from 'next/head';
import Router from 'next/router';
import { SessionProvider } from 'next-auth/react';
import { useEffect, Fragment } from 'react';
import { CookiesProvider } from 'react-cookie';
import { EngagementEvents, logKariusEvent } from '../src/Analytics';
import { sessionTimeoutMaxAge } from '../src/Config/auth';
import { PublicUrls } from '../src/Config/links';
import SWRConfigProvider from '../src/Config/SWR/SWRConfigProvider';
import { HolidayMessage } from '../src/EtrfOrder/OrderComponents/HolidayMessage';
import type { AppPropsWithLayout } from '../src/page/page';
import { GlobalSnackProvider } from '../src/Snack/GlobalSnack';
import createEmotionCache from '../src/theme/createEmotionCache';
import theme from '../src/theme/theme';
import '../styles/globals.css';

// Client-side cache, shared for the whole session of the user in the browser.
const clientSideEmotionCache = createEmotionCache();

interface AppProps extends AppPropsWithLayout {
  emotionCache: ReturnType<typeof createEmotionCache>;
}

function MyApp({ Component, pageProps, emotionCache = clientSideEmotionCache }: AppProps) {
  useEffect(() => {
    let refreshSessionCheckingId: ReturnType<typeof setTimeout>;
    const refreshSession = throttle(() => {
      if (PublicUrls.includes(window.location.pathname)) {
        return; // no need to check session for url that not requires login
      }
      fetch('/api/auth/session').then((s) => {
        s.json().then((j) => {
          if (!j || !j.user) {
            logKariusEvent(EngagementEvents.inactivity_sign_out);
            let asPath = Router.router?.asPath ?? '';
            // if it is protected page, then redirect to login page if user session times out
            Router.push(`/login?returnToPath=${encodeURIComponent(asPath)}`);
          }
        });
      });
      if (refreshSessionCheckingId) clearTimeout(refreshSessionCheckingId);
      // always check if user still in after the max age time
      refreshSessionCheckingId = setTimeout(() => {
        if (window.document.hasFocus()) {
          refreshSession();
        }
      }, (sessionTimeoutMaxAge + 30) * 1000); // set checking once session should time out
    }, 5 * 1000);

    const eventTypes = ['keydown', 'click', 'scroll'];
    eventTypes.forEach((eventType) => {
      window.document.addEventListener(eventType, refreshSession);
    });

    window.onfocus = function () {
      // when user switch back to this tab
      refreshSession();
    };

    return () => {
      eventTypes.forEach((eventType) => {
        window.document.removeEventListener(eventType, refreshSession);
      });
    };
  }, []);

  useEffect(() => {
    // Remove the server-side injected CSS.
    const jssStyles = document.querySelector('#jss-server-side');
    if (jssStyles) {
      jssStyles.parentElement!.removeChild(jssStyles);
    }
  }, []);

  const getLayout = Component.getLayout || ((page) => page);
  return (
    <Fragment>
      <Head>
        <title>Karius Online</title>
        <meta name='viewport' content='minimum-scale=1, initial-scale=1, width=device-width' />
      </Head>
      <CacheProvider value={emotionCache}>
        <SessionProvider>
          <CookiesProvider>
            <SWRConfigProvider>
              <ThemeProvider theme={theme}>
                <GlobalSnackProvider>
                  {/* CssBaseline kickstart an elegant, consistent, and simple baseline to build upon. */}
                  <CssBaseline />
                  {getLayout(<Component {...pageProps} />, pageProps?.resourceName)}
                  {/* Messages and Notifications */}
                  <HolidayMessage />
                </GlobalSnackProvider>
              </ThemeProvider>
            </SWRConfigProvider>
          </CookiesProvider>
        </SessionProvider>
      </CacheProvider>
    </Fragment>
  );
}

export default MyApp;
